/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Store, select } from '@ngrx/store';
import { map, withLatestFrom } from 'rxjs/operators';
import { EditStateService } from 'src/app/features/learner-profile/services/edit-state.service';
import { FeatureFlagService } from '../../services/feature-flags/feature-flag.service';
import { AnnouncementsService } from '../../services/communication/announcements.service';
import { SupportService } from '../../services/support/support.service';
import { AcademicClassActions } from '../academics/academic.actions';
import { ActivityActions } from '../activities/activity.actions';
import { EventActions } from '../events/event.actions';
import { FinanceActions } from '../finance/finance.actions';
import { GuardianActions } from '../guardian/guardian.actions';
import { selectAccountHolderAccountLinks } from '../guardian/guardian.selectors';
import { ResourceActions } from '../resources/resource.actions';
import { SchoolActions } from '../schools/school.actions';
import { StaffActions } from '../staff/staff.actions';
import { StudentActions } from '../students/student.actions';
import { selectRouteParams, selectUrl } from './router.selectors';

@Injectable()
export class RouterEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private announcementService: AnnouncementsService,
    private supportService: SupportService,
    private editStateService: EditStateService,
    private featureFlags: FeatureFlagService,
  ) {}

  routeChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROUTER_NAVIGATED),
        withLatestFrom(this.store.pipe(select(selectRouteParams))),
        map(([action, params]) => {
          Object.entries(params).forEach(([paramName, paramValue]) => {
            // Use a switch statement to dispatch actions based on param names
            switch (paramName) {
              case 'guardianid':
                this.store.dispatch(GuardianActions.selectGuardian({ id: paramValue }));
                break;
              case 'studentid':
                this.store.dispatch(StudentActions.selectStudent({ id: paramValue }));
                break;
              case 'staffid':
                this.store.dispatch(StaffActions.selectStaff({ id: paramValue }));
                break;
              case 'taskid':
                break;
              case 'schoolid':
                this.store.dispatch(SchoolActions.selectSchool({ id: paramValue }));
                break;
              case 'eventid':
                this.store.dispatch(EventActions.selectEvent({ id: paramValue }));
                break;
              case 'activityid':
                this.store.dispatch(ActivityActions.selectActivity({ id: paramValue }));
                break;
              case 'classid':
                this.store.dispatch(AcademicClassActions.selectClass({ id: paramValue }));
                break;
              case 'folderid':
                this.store.dispatch(ResourceActions.selectFolder({ folderId: paramValue }));
                break;
              case 'messageid':
                this.announcementService.setSelectedMessage({ id: paramValue });
                break;
              case 'ticketid':
                this.supportService.setSelectedTicket({ id: paramValue });
                break;
              case 'schoolCode':
                this.store.dispatch(ResourceActions.selectSchool({ schoolCode: paramValue }));
                this.store.dispatch(ResourceActions.loadFolders({ schoolCode: paramValue }));
                break;
              default:
                break;
            }
          });
        }),
      ),
    { dispatch: false },
  );

  routePathChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROUTER_NAVIGATED),
        withLatestFrom(
          this.store.pipe(select(selectUrl)),
          this.store.pipe(select(selectAccountHolderAccountLinks)),
          this.store.pipe(select(selectRouteParams)),
        ),
        map(([action, url, accountLinks, params]) => {
          // Reset edit state on any route change
          this.editStateService.isEditing.set(false);

          // Check for finance/payment routes
          if (url.startsWith('/payments')) {
            // Load financial data using the company codes from the logged in guardian
            this.store.dispatch(FinanceActions.loadBankingDetails({ accountLinks }));
            if (this.featureFlags.get('debitOrders')) {
              this.store.dispatch(FinanceActions.loadDebitOrders());
            }
            this.store.dispatch(FinanceActions.loadBankRecords({ accountLinks }));
            this.store.dispatch(FinanceActions.loadAccountHolderDetails());
          }

          // Check for attendance/student profile routes
          if (url.includes('/attendance') && params['studentid']) {
            this.store.dispatch(StudentActions.loadStudentAttendance({ studentId: params['studentid'] }));
          }
        }),
      ),
    { dispatch: false },
  );
}
